import { useEffect, useState } from "react";

import { useNavigate } from "react-router";

import { LedgerAccountSelector } from "@/components/pieces/products/LedgerAccountSelector";
import { Button } from "@/components/ui/button";
import {
  CreateMeetingStatus,
  GetCoachingProgramsQuery,
  LedgerAccountType,
  ProductCategories,
  useBookReviewMutation,
  useCreateCoachMeetingMutation,
  useGetLedgerAccountsQuery,
} from "@/graphql";
import {
  formatCredits,
  formatCreditsLowerCase,
  parseDecimal,
} from "@/utils/string";

type Program = GetCoachingProgramsQuery["coachingPrograms"]["programs"][number];
type Product = Program["sessions"][number]["product"];
type CoachProduct = Program["coaches"][number]["coachProduct"];

interface BookClientActionProps {
  coachId: string;
  clientId?: string;
  product?: Product;
  coachProduct?: CoachProduct;
  selectedWorkspaceId: string | null;
  sessionId?: string;
  timeslots: string[];
  showAllTimes: boolean;
  errorMessage: string | null;
  onSetErrorMessage: (error: string | null) => void;
}

export function BookClientAction({
  clientId,
  coachId,
  product,
  coachProduct,
  selectedWorkspaceId,
  sessionId,
  timeslots,
  showAllTimes,
  errorMessage,
  onSetErrorMessage,
}: BookClientActionProps) {
  const navigate = useNavigate();
  const [account, setAccount] = useState<LedgerAccountType>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const category = product?.category;

  const { data, loading: accountsLoading } = useGetLedgerAccountsQuery({
    variables: {
      customerId: clientId ?? "",
      workspaceId: selectedWorkspaceId,
    },
    skip: !clientId,
  });

  const accounts = data?.ledgerAccounts ?? [];

  useEffect(() => {
    setAccount(accounts[0]);
  }, [accounts]);

  const [createCoachMeeting, { loading }] = useCreateCoachMeetingMutation({
    update: (cache) => {
      cache.evict({ fieldName: "customer" });
      cache.evict({ fieldName: "availableCoaches" });
      cache.gc();
    },
  });

  const [bookReview] = useBookReviewMutation({
    update: (cache) => {
      cache.evict({ fieldName: "customer" });
      cache.evict({ fieldName: "availableCoaches" });
      cache.gc();
    },
  });

  const handleSubmit = async () => {
    if (!product) return;
    const productId = product?.id;

    setIsSubmitting(true);

    try {
      if (category === ProductCategories.Review) {
        const { data } = await bookReview({
          variables: {
            coachId,
            productId,
            ledgerAccountId: account?.id,
            customerId: clientId,
            workspaceId:
              selectedWorkspaceId !== "-1" ? selectedWorkspaceId : undefined,
          },
        });

        if (!data?.bookReview?.error) {
          navigate(`/client/${clientId}/reviews`);
        } else {
          setIsSubmitting(false);
          onSetErrorMessage(data.bookReview.error);
        }
      } else {
        const timeslotsStrings = timeslots.map(
          (timeslot) => `${timeslot},False`,
        );

        const { data } = await createCoachMeeting({
          variables: {
            coachId,
            productId,
            startTime: timeslots[0] ?? "",
            ledgerAccountId: account?.id,
            formData: {},
            customerId: clientId,
            timeslotsStrings,
            usedAllTimeslot: showAllTimes,
            programComponentId: sessionId,
            workspaceId:
              selectedWorkspaceId !== "-1" ? selectedWorkspaceId : undefined,
          },
        });

        const { meeting, status } = data?.createCoachMeeting || {};
        if (status === CreateMeetingStatus.Success && meeting) {
          navigate(`/client/${clientId}/sessions`);
        } else {
          onSetErrorMessage(status ?? null);
        }
        setIsSubmitting(false);
      }
    } catch {
      onSetErrorMessage(
        "An error occurred while booking. Please contact support.",
      );
      setIsSubmitting(false);
    }
  };

  if (!product || !coachProduct) return null;

  // Check for account-related errors
  const requiredCredits = parseDecimal(coachProduct?.coachPrice);
  const hasNoAccounts = !accountsLoading && accounts.length === 0;
  const hasNoCredits =
    !accountsLoading &&
    accounts.length > 0 &&
    accounts.every((acc) => parseDecimal(acc.balance) < requiredCredits);
  const selectedAccountInsufficientCredits =
    account && parseDecimal(account.balance) < requiredCredits;
  const hasAccountError =
    hasNoAccounts || hasNoCredits || selectedAccountInsufficientCredits;
  const creditText = formatCreditsLowerCase(coachProduct?.coachPrice);

  // Button state
  const needsTimeslots =
    category !== ProductCategories.Review && timeslots.length === 0;
  const isDisabled =
    loading || !account || hasAccountError || needsTimeslots || isSubmitting;

  return (
    <div className="flex flex-col gap-3 mt-4">
      <div className="w-full rounded-lg border border-gray-300 bg-white p-6 font-normal">
        {hasNoAccounts ? (
          <div className="text-center py-8">
            <div className="text-gray-600 mb-2">
              No Payment Accounts Available
            </div>
            <div className="text-sm text-gray-500">
              This client doesn't have any payment accounts set up.
            </div>
          </div>
        ) : hasNoCredits ? (
          <div className="text-center py-8">
            <div className="text-gray-600 mb-2">Insufficient Credits</div>
            <div className="text-sm text-gray-500">
              All payment accounts have insufficient credits for this booking.
            </div>
          </div>
        ) : (
          <>
            <LedgerAccountSelector
              ledgerAccounts={accounts}
              ledgerAccountId={account?.id}
              onSelectAccountId={(id: string) =>
                setAccount(accounts?.find((a) => a.id === id))
              }
            />
            {selectedAccountInsufficientCredits && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <div className="text-amber-800 text-sm">
                  <strong>Insufficient credits:</strong> This account has{" "}
                  {formatCredits(account.balance)} but requires {creditText} for
                  this booking.
                </div>
              </div>
            )}
            <div className="mt-6">
              <div>
                <b>{creditText}</b> will be deducted.
              </div>
              <div className="mt-6">
                If you cancel this meeting within the allowed cancellation
                window you will receive a credit refund.
              </div>
            </div>
          </>
        )}
      </div>

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm">
          {errorMessage}
        </div>
      )}

      <div className="flex justify-center mt-2">
        <Button
          onClick={handleSubmit}
          size="lg"
          variant="solid"
          disabled={isDisabled}
          className="w-full"
        >
          {isSubmitting && (
            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
          )}
          {isSubmitting
            ? "Booking..."
            : hasAccountError
              ? "Cannot Book"
              : "Book Now"}
        </Button>
      </div>
    </div>
  );
}
